import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import minusIcon from '@/assets/icons/minus.svg';
import addIcon from '@/assets/icons/add.svg';
import PersonFields from './PersonFields';
import { TextField } from './FormField';
import { toHebrewName } from '@/helpers/hebrewName';
import { FileField } from './FileField';
import AdditionalDocumentsField from './AdditionalDocumentsField';
import { useEditMode } from '../EditModeContext';
import { useOpenFundFieldTracking } from '../OpenFundAnalyticsContext';
import {
  createDefaultCoordinator,
  CoordinatorRelationToSupportedOptions,
  MAX_COORDINATORS,
  OpenFundFormData,
} from '../schemas/openFundFormSchema';
import styles from './ArrayFields.module.scss';
import fieldStyles from './FormField.module.scss';

interface CoordinatorFieldsProps {
  allowRemoveLast?: boolean;
  onLastRemoved?: () => void;
  showRelationToSupported?: boolean;
}

const CoordinatorFields = ({
  allowRemoveLast = false,
  onLastRemoved,
  showRelationToSupported = false,
}: CoordinatorFieldsProps) => {
  const { t } = useTranslation();
  const isEditMode = useEditMode();
  const { control } = useFormContext<OpenFundFormData>();
  const trackField = useOpenFundFieldTracking();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'coordinators',
  });

  const canRemove = (index: number) =>
    !isEditMode && (fields.length > 1 || (allowRemoveLast && index === 0));

  const canAdd = !isEditMode && fields.length < MAX_COORDINATORS;

  const handleRemove = (index: number) => {
    const isLast = fields.length === 1;
    remove(index);
    if (isLast && onLastRemoved) onLastRemoved();
  };

  return (
    <div className={styles.wrapper}>
      {fields.map((field, index) => (
        <div className={styles.entry} key={field.id}>
          <div className={styles.entryHeader}>
            <span className="body-lr-semibold text-(--font-heading)">
              {t('openFundRequest.fields.coordinatorDetails')} {index + 1}
            </span>
            {canRemove(index) && (
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => handleRemove(index)}
                aria-label={t('openFundRequest.buttons.removeCoordinator')}
              >
                <img src={minusIcon} alt="" />
              </button>
            )}
          </div>
          <PersonFields namePrefix={`coordinators.${index}`} />
          {showRelationToSupported && (
            <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
              <div className={fieldStyles.fieldWrapper}>
                <label className={`body-sm-regular ${fieldStyles.label}`}>
                  {t('openFundRequest.fields.relationToSupported')}
                </label>
                <Controller
                  control={control}
                  name={`coordinators.${index}.relationToSupported` as never}
                  render={({ field }) => {
                    const fieldName = `coordinators.${index}.relationToSupported`;
                    return (
                      <select
                        {...field}
                        onFocus={() => trackField(fieldName)}
                        onChange={(event) => {
                          field.onChange(event);
                          trackField(fieldName);
                        }}
                        className={`${fieldStyles.input} body-md-regular`}
                      >
                      <option value="">{t('openFundRequest.fields.selectOption')}</option>
                      {CoordinatorRelationToSupportedOptions.map((option) => (
                        <option key={option} value={option}>
                          {t(`openFundRequest.coordinatorRelationOptions.${option}`)}
                        </option>
                      ))}
                      </select>
                    );
                  }}
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <FileField
              name={`coordinators.${index}.idCardPhotocopy`}
              label={t('openFundRequest.fields.idCardPhotocopy')}
            />
            <FileField
              name={`coordinators.${index}.idAppendixPhotocopy`}
              label={t('openFundRequest.fields.idAppendixPhotocopy')}
            />
          </div>
          <AdditionalDocumentsField namePrefix={`coordinators.${index}.additionalDocuments`} />
          <div>
            <span className="body-lr-semibold text-(--font-heading)">
              {t('openFundRequest.fields.spouseDetails')}
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '6px' }}>
              <TextField
                name={`coordinators.${index}.spouseFirstName` as never}
                label={t('openFundRequest.fields.spouseFirstName')}
                transform={toHebrewName}
              />
              <TextField
                name={`coordinators.${index}.spouseIsraeliId` as never}
                label={t('openFundRequest.fields.spouseIsraeliId')}
              />
            </div>
          </div>
        </div>
      ))}
      {canAdd && (
        <button
          type="button"
          className={styles.addButton}
          onClick={() => append(createDefaultCoordinator())}
        >
          <img src={addIcon} alt="" />
          {t('openFundRequest.buttons.addCoordinator')}
        </button>
      )}
    </div>
  );
};

export default CoordinatorFields;
